import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import {
  addCivilCasePowerOfAgency,
  editCivilCasePowerOfAgency,
} from "services/civilCaseComplaintDetailService";
import { CivilCasePowerOfAgency } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm, formatPhoneNumber } from "utils/helper";

type Props = {
  isOpen: boolean;
  action: "Add" | "Edit";
  powerOfAgency?: CivilCasePowerOfAgency;
  complaintId: number | string;
  onClose: (refetch: boolean) => void;
};

const defaultData: CivilCasePowerOfAgency = {
  name: "",
  region: "",
  zone_or_city: "",
  woreda_or_kebele: "",
  phone_number: "",
  email: "",

  sub_city: "",
  kebele: "",
  house_number: "",
};

const CivilCasePowerOfAgencyForm: FC<Props> = ({
  isOpen,
  action,
  powerOfAgency,
  complaintId,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const [formState, setFormState] = useState<CivilCasePowerOfAgency>(
    powerOfAgency ?? defaultData,
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (powerOfAgency) {
      setFormState(powerOfAgency);
    }
  }, [powerOfAgency]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState.name) return "Name is Required.";
    else if (!formState.phone_number) return "Phone Number is Required.";
    else if (formState?.phone_number.length < 10)
      return "Please Enter a Correct Phone Number.";
    else if (
      formState.email &&
      formState.email &&
      !/\S+@\S+\.\S+/.test(formState.email)
    )
      return "Email is Invalid.";

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const submitAction =
      action === "Edit"
        ? editCivilCasePowerOfAgency
        : addCivilCasePowerOfAgency;
    submitAction({
      ...formState,
      complaint: complaintId,
    })
      .then(() => {
        showTooltip(
          action === "Edit"
            ? "Successfully Updated!"
            : "Successfully Submitted!",
          "success",
        );
        setFormState(defaultData);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-lg p-6 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <div className="w-1 h-6 bg-red-600 rounded-full"></div>
          <h2 className="text-lg font-semibold text-gray-900">
            {action} Power of Agency
          </h2>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">
                Name *
              </label>
              <input
                type="text"
                className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                name="name"
                value={formState?.name}
                onChange={handleChange}
                placeholder="Enter agency or representative name"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">
                Phone Number *
              </label>
              <input
                type="tel"
                className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                name="phone_number"
                value={formState?.phone_number ? formState?.phone_number : ""}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  setFormState((prevState) => ({
                    ...prevState,
                    phone_number: formatted,
                  }));
                }}
                placeholder="Enter phone number"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">Email</label>
              <input
                type="email"
                className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                name="email"
                value={formState?.email ? formState?.email : ""}
                onChange={handleChange}
                placeholder="Enter email address"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">
                Region
              </label>
              <input
                className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                name="region"
                value={formState?.region}
                onChange={handleChange}
                placeholder="Enter region"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">
                Zone / City
              </label>
              <input
                className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                name="zone_or_city"
                value={formState?.zone_or_city}
                onChange={handleChange}
                placeholder="Enter zone or city"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">
                Woreda / Kebele
              </label>
              <input
                className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                name="woreda_or_kebele"
                value={formState?.woreda_or_kebele}
                onChange={handleChange}
                placeholder="Enter woreda"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">
                Sub City
              </label>
              <input
                className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                name="sub_city"
                value={formState?.sub_city}
                onChange={handleChange}
                placeholder="Enter sub city"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">
                Kebele
              </label>
              <input
                className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                name="kebele"
                value={formState?.kebele}
                onChange={handleChange}
                placeholder="Enter kebele"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">
                House Number
              </label>
              <input
                className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                name="house_number"
                value={formState?.house_number}
                onChange={handleChange}
                placeholder="Enter house number"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 mr-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all flex items-center shadow-sm hover:shadow"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                `${action} Power of Agency`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CivilCasePowerOfAgencyForm;
